/**
 * P8.1: Manifest Field Instrumentation
 *
 * Tracks three metrics per content manifest field:
 * - ManifestField_Used: Does the LLM produce a non-default value?
 * - ManifestField_Engagement: Do users interact more when personalized?
 * - ManifestField_Latency: How much time does this field add to generation?
 *
 * These metrics drive the P8.2 classification decision:
 * high-value (>15% lift) → keep LLM, low-value (<5% lift) → migrate to template.
 *
 * Ref: .claude/docs/SDUI_ADOPTION_GUIDE.md Phase 8
 */

import { useEffect, useRef } from "react";
import type { ContentManifest } from "@dramadetector/shared";
import { captureEvent } from "../../lib/analytics";

const MANIFEST_FIELDS = [
  "narratorOverride",
  "emphasisLevel",
  "customInsights",
] as const;

type ManifestField = (typeof MANIFEST_FIELDS)[number];

function differsFromDefault(field: ManifestField, value: unknown): boolean {
  if (field === "emphasisLevel") return value !== undefined && value !== "normal";
  return value !== undefined;
}

interface ManifestInstrumentationParams {
  manifest: ContentManifest | null;
  analysisId: string | undefined;
  tier: string;
  manifestLoadTimeMs?: number;
}

/**
 * Instruments manifest fields with PostHog events.
 *
 * Fires ManifestField_Used once per render when manifest is available.
 * Fires ManifestField_Engagement on unmount with time-in-view and
 * differs_from_default so P8.2 can compute engagement lift.
 */
export function useManifestInstrumentation({
  manifest,
  analysisId,
  tier,
  manifestLoadTimeMs,
}: ManifestInstrumentationParams): void {
  const mountTimeRef = useRef<number>(Date.now());
  const trackedRef = useRef<boolean>(false);

  // Track field usage (fires once when manifest arrives)
  useEffect(() => {
    if (!manifest || !analysisId || trackedRef.current) return;
    trackedRef.current = true;

    for (const field of MANIFEST_FIELDS) {
      const value = manifest[field];

      captureEvent("ManifestField_Used", {
        field,
        differs_from_default: differsFromDefault(field, value),
        analysis_id: analysisId,
        tier,
      });
    }

    // Track latency if available
    if (manifestLoadTimeMs !== undefined) {
      captureEvent("ManifestField_Latency", {
        latency_ms: manifestLoadTimeMs,
        analysis_id: analysisId,
        tier,
        has_narrator: !!manifest.narratorOverride,
        has_insights: (manifest.customInsights?.length ?? 0) > 0,
      });
    }
  }, [manifest, analysisId, tier, manifestLoadTimeMs]);

  // Track engagement on unmount (time spent viewing)
  useEffect(() => {
    return () => {
      if (!manifest || !analysisId) return;

      const timeVisibleMs = Date.now() - mountTimeRef.current;

      for (const field of MANIFEST_FIELDS) {
        const value = manifest[field];
        if (value === undefined) continue;

        captureEvent("ManifestField_Engagement", {
          field,
          differs_from_default: differsFromDefault(field, value),
          time_visible_ms: timeVisibleMs,
          analysis_id: analysisId,
          tier,
        });
      }
    };
  }, [manifest, analysisId, tier]);
}
